/**
 * Renders the real document with PDF.js.
 *
 * Each page is drawn into a canvas that already lives inside its `data-page`
 * section, so the reading tracker's IntersectionObserver keeps measuring real
 * on-screen geometry — the canvas gives each page its true height instead of the
 * placeholder skeleton.
 */
import * as pdfjs from 'pdfjs-dist';
// Vite resolves this to a hashed asset URL and serves it same-origin, so the
// worker loads under the strict Pages CSP without a CDN.
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

// Retina renders at 2x for crispness; capping there keeps 12 canvases off the
// memory ceiling on a phone.
const MAX_DPR = 2;

/**
 * Load the PDF at `url` and paint each page into the matching canvas.
 * `canvasFor(n)` returns the canvas for 1-based page `n` (or null if unmounted).
 * `signal` lets a React cleanup abort a render that's no longer needed.
 */
export async function renderDocument(
  url: string,
  canvasFor: (page: number) => HTMLCanvasElement | null,
  signal?: AbortSignal,
): Promise<void> {
  const pdf = await pdfjs.getDocument({ url, withCredentials: false }).promise;
  try {
    for (let n = 1; n <= pdf.numPages; n++) {
      if (signal?.aborted) return;
      const canvas = canvasFor(n);
      if (!canvas) continue;

      const page = await pdf.getPage(n);
      const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      // Fit the page to the canvas's content-box width; height follows aspect.
      const cssWidth = canvas.parentElement?.clientWidth ?? 640;
      const unscaled = page.getViewport({ scale: 1 });
      const viewport = page.getViewport({ scale: (cssWidth * dpr) / unscaled.width });

      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      canvas.style.width = '100%';
      canvas.style.height = 'auto';

      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, viewport }).promise;
    }
  } finally {
    pdf.destroy();
  }
}
